import { AfterViewChecked, Component, ElementRef, ViewChild, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { getAuth } from 'firebase/auth';
import { marked } from 'marked';
import { AuthService } from '../../core/services/auth.service';
import { OrgContextService } from '../../core/services/org-context.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { TranslateService } from '../../core/i18n/translate.service';
import { SupportChatbotService } from './support-chatbot.service';
import { environment } from '../../../environments/environment';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const FUNCTIONS_REGION = 'northamerica-northeast1';

@Component({
  selector: 'app-support-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  styles: [`
    :host { display: contents; }
    .sc-panel {
      position: fixed; bottom: 20px; right: 20px; z-index: 1000;
      width: 22rem; max-width: calc(100vw - 2rem);
      height: 28rem; max-height: calc(100vh - 6rem);
      background: var(--background-color); color: var(--text-color);
      border: 1px solid var(--border-color);
      border-radius: 12px; box-shadow: 0 25px 50px rgba(0,0,0,.25);
      display: flex; flex-direction: column; overflow: hidden;
    }
    .sc-header { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; border-bottom:1px solid var(--border-color); font-weight:600; font-size:14px; color:var(--text-color); }
    .sc-header button { background:none; border:none; cursor:pointer; color:var(--text-secondary-color); padding:4px; border-radius:4px; }
    .sc-header button:hover { background:var(--surface-color); }
    .sc-body { flex:1; overflow-y:auto; padding:12px 14px; display:flex; flex-direction:column; gap:10px; font-size:14px; }
    .sc-empty { text-align:center; color:var(--text-secondary-color); margin-top:32px; }
    .sc-row { display:flex; }
    .sc-row.user { justify-content:flex-end; }
    .sc-bubble-user {
      display:inline-block; max-width:85%; padding:8px 12px; border-radius:1rem;
      background: var(--surface-color); color: var(--text-color);
      white-space:pre-wrap; word-break:break-word; line-height:1.5;
    }
    .sc-assistant { max-width:100%; color: var(--text-color); font-size:14px; line-height:1.5; word-break:break-word; }
    .sc-assistant :first-child { margin-top:0; }
    .sc-assistant :last-child  { margin-bottom:0; }
    .sc-assistant p { margin: 0.5rem 0; }
    .sc-assistant h1, .sc-assistant h2, .sc-assistant h3,
    .sc-assistant h4, .sc-assistant h5, .sc-assistant h6 {
      font-weight: 600; margin: 0.85rem 0 0.35rem; line-height: 1.3;
    }
    .sc-assistant h1 { font-size: 1.1rem; }
    .sc-assistant h2 { font-size: 1.02rem; }
    .sc-assistant h3 { font-size: 0.95rem; }
    .sc-assistant h4, .sc-assistant h5, .sc-assistant h6 { font-size: 0.9rem; }
    .sc-assistant ul, .sc-assistant ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }
    .sc-assistant ul { list-style: disc outside; }
    .sc-assistant ol { list-style: decimal outside; }
    .sc-assistant ul ul { list-style: circle outside; }
    .sc-assistant ul ul ul { list-style: square outside; }
    .sc-assistant ul ul, .sc-assistant ol ol,
    .sc-assistant ul ol, .sc-assistant ol ul {
      margin: 0.25rem 0;
    }
    .sc-assistant li { margin: 0.2rem 0; padding-left: 0.15rem; }
    .sc-assistant li > p { margin: 0.2rem 0; }
    .sc-assistant li::marker { color: var(--text-secondary-color); }
    .sc-assistant a { color: #4c17ca; text-decoration: underline; text-underline-offset: 2px; }
    .sc-assistant a:hover { text-decoration-thickness: 2px; }
    .sc-assistant strong { font-weight: 600; }
    .sc-assistant em { font-style: italic; }
    .sc-assistant code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.85em;
      background: rgba(127, 127, 127, 0.18);
      padding: 0.1em 0.35em;
      border-radius: 0.25rem;
    }
    .sc-assistant pre {
      background: rgba(127, 127, 127, 0.15);
      padding: 0.65rem 0.75rem;
      border-radius: 0.5rem;
      overflow-x: auto;
      margin: 0.6rem 0;
      font-size: 0.85em;
      line-height: 1.5;
    }
    .sc-assistant pre code {
      background: transparent;
      padding: 0;
      font-size: 1em;
    }
    .sc-assistant blockquote {
      border-left: 3px solid rgba(127, 127, 127, 0.4);
      padding: 0.1rem 0 0.1rem 0.65rem;
      margin: 0.5rem 0;
      color: var(--text-secondary-color);
    }
    .sc-assistant hr { border: none; border-top: 1px solid rgba(127, 127, 127, 0.3); margin: 0.75rem 0; }
    .sc-assistant table {
      border-collapse: collapse;
      margin: 0.5rem 0;
      font-size: 0.9em;
      width: 100%;
    }
    .sc-assistant th, .sc-assistant td {
      border: 1px solid rgba(127, 127, 127, 0.3);
      padding: 0.3rem 0.5rem;
      text-align: left;
    }
    .sc-assistant th {
      background: rgba(127, 127, 127, 0.1);
      font-weight: 600;
    }
    .sc-error { color:#ef4444; font-size:12px; }
    .sc-notice { font-size:12px; color: var(--text-secondary-color); padding:8px; }
    .sc-notice a { color: var(--accent-color); font-weight:500; }
    .sc-footer { padding:8px; }
    .sc-input-wrap {
      display:flex; align-items:flex-end; gap:8px;
      width:100%; padding: 6px 8px;
      border:1px solid var(--border-color); border-radius:0.5rem;
      background: color-mix(in srgb, var(--surface-color) 50%, transparent);
      backdrop-filter: blur(4px);
      transition: border-color 0.15s;
    }
    .sc-input-wrap:hover,
    .sc-input-wrap:focus-within {
      border-color: color-mix(in srgb, var(--text-secondary-color) 40%, transparent);
    }
    .sc-input-wrap textarea {
      flex:1; resize:none; border:none; background:transparent;
      color:var(--text-color); font-size:14px; padding:4px 0; max-height:160px; outline:none;
      font-family:inherit; line-height:1.375;
    }
    .sc-input-wrap textarea::placeholder { color:var(--placeholder-color); }
    .sc-send {
      background:#4c17ca; color:#fff; border:none; border-radius:9999px;
      width:32px; height:32px; display:inline-flex; align-items:center; justify-content:center;
      cursor:pointer; transition: background-color .15s; padding:0; flex-shrink:0;
    }
    .sc-send:hover:not(:disabled) { background:#3d12a2; }
    .sc-send:active:not(:disabled) { background:#2e0e7a; }
    .sc-send:disabled { background: var(--border-color); color: var(--text-secondary-color); cursor: default; }
    .sc-send svg { width:20px; height:20px; }
    .sc-send .sc-spinner {
      width:16px; height:16px; border-radius:9999px;
      border:2px solid currentColor; border-top-color: transparent;
      animation: sc-spin 1s linear infinite;
    }
    @keyframes sc-spin { to { transform: rotate(360deg); } }
    .sc-typing { display:inline-flex; align-items:center; gap:4px; vertical-align:middle; }
    .sc-typing-dot {
      display:inline-block; width:6px; height:6px; border-radius:9999px;
      background-color: var(--text-secondary-color);
      animation: sc-typing 1.4s infinite;
    }
    .sc-typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .sc-typing-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes sc-typing {
      0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
      30%           { opacity: 1;   transform: translateY(-2px); }
    }
  `],
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (authed()) {
      @if (open()) {
        <div class="sc-panel">
          <div class="sc-header">
            <span>{{ 'support.title' | t }}</span>
            <div style="display:flex;gap:4px;">
              @if (sending()) {
                <button type="button" (click)="cancel()" [attr.aria-label]="'support.stop' | t" style="font-size:12px;padding:2px 8px;cursor:pointer;">
                  {{ 'support.stop' | t }}
                </button>
              }
              <button type="button" (click)="toggle()" [attr.aria-label]="'support.closeAria' | t">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
              </button>
            </div>
          </div>
          <div #scroll class="sc-body">
            @if (messages().length === 0 && hasOrg() && hasCredits()) {
              <div class="sc-empty">{{ 'support.greeting' | t }}</div>
            }
            @for (msg of messages(); track $index) {
              @if (msg.role === 'user') {
                <div class="sc-row user">
                  <div class="sc-bubble-user">{{ msg.content }}</div>
                </div>
              } @else {
                <div class="sc-assistant">
                  @if (msg.content) {
                    <div [innerHTML]="renderMarkdown(msg.content)"></div>
                  } @else if (sending()) {
                    <span class="sc-typing" aria-hidden="true">
                      <span class="sc-typing-dot"></span>
                      <span class="sc-typing-dot"></span>
                      <span class="sc-typing-dot"></span>
                    </span>
                  }
                </div>
              }
            }
            @if (errorMsg()) { <div class="sc-error">{{ errorMsg() }}</div> }
          </div>
          <div class="sc-footer">
            @if (!hasOrg()) {
              <div class="sc-notice">
                {{ 'support.noOrg' | t }}
                <a [href]="adminConsoleUrl + '/new'" target="_blank" rel="noopener">
                  {{ 'support.noOrgCta' | t }}
                </a>
              </div>
            } @else if (!hasCredits()) {
              <div class="sc-notice">
                {{ 'support.noCreditsLead' | t }}<a [href]="adminConsoleUrl + '/billing'" target="_blank" rel="noopener">{{ 'support.noCreditsLink' | t }}</a>{{ 'support.noCreditsTail' | t }}
              </div>
            } @else {
              <div class="sc-input-wrap">
                <textarea
                  #ta
                  [(ngModel)]="input"
                  (keydown)="onKeydown($event)"
                  (input)="autoGrow(ta)"
                  [disabled]="sending()"
                  rows="1"
                  [placeholder]="'support.placeholder' | t"
                ></textarea>
                <button type="button" class="sc-send" (click)="send(ta)" [disabled]="sending() || !input.trim()" [attr.aria-label]="'support.send' | t">
                  @if (sending()) {
                    <span class="sc-spinner"></span>
                  } @else {
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 17a.75.75 0 0 1-.75-.75V5.56L5.22 9.59a.75.75 0 1 1-1.04-1.08l5.25-5.25a.75.75 0 0 1 1.04 0l5.25 5.25a.75.75 0 1 1-1.04 1.08L10.75 5.56v10.69A.75.75 0 0 1 10 17Z" clip-rule="evenodd" />
                    </svg>
                  }
                </button>
              </div>
            }
          </div>
        </div>
      }
    }
  `,
})
export class SupportChatbotComponent implements AfterViewChecked {
  private auth = inject(AuthService);
  private orgContext = inject(OrgContextService);
  private translate = inject(TranslateService);
  private supportChatbot = inject(SupportChatbotService);

  @ViewChild('scroll') scroll?: ElementRef<HTMLDivElement>;

  readonly adminConsoleUrl = environment.adminConsoleUrl;
  readonly open = this.supportChatbot.open;
  readonly messages = signal<ChatMessage[]>([]);
  readonly sending = signal(false);
  readonly errorMsg = signal('');
  readonly authed = computed(() => this.auth.isAuthenticated());
  readonly hasOrg = computed(() => !!this.orgContext.currentOrg()?.id);
  readonly hasCredits = computed(() => Number(this.orgContext.currentOrg()?.creditBalance ?? 0) > 0);

  input = '';
  private shouldScroll = false;
  private sessionId: string | null = null;
  private abortController: AbortController | null = null;
  private currentRunId: string | null = null;

  toggle() {
    const willOpen = !this.open();
    this.supportChatbot.toggle();
    if (willOpen) {
      if (!this.sessionId) this.sessionId = this.generateSessionId();
    } else {
      this.resetSession();
    }
    this.shouldScroll = true;
  }

  ngAfterViewChecked() {
    if (this.shouldScroll && this.scroll) {
      this.scroll.nativeElement.scrollTop = this.scroll.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }

  autoGrow(ta: HTMLTextAreaElement) {
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }

  onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  cancel() {
    this.abortController?.abort();
  }

  renderMarkdown(text: string): string {
    return marked.parse(text, { async: false, breaks: true, gfm: true }) as string;
  }

  async send(ta?: HTMLTextAreaElement) {
    const text = this.input.trim();
    if (!text || this.sending()) return;
    if (!this.sessionId) this.sessionId = this.generateSessionId();

    this.errorMsg.set('');
    const next: ChatMessage[] = [
      ...this.messages(),
      { role: 'user', content: text },
      { role: 'assistant', content: '' },
    ];
    this.messages.set(next);
    this.input = '';
    if (ta) ta.style.height = '';
    this.sending.set(true);
    this.shouldScroll = true;

    try {
      await this.stream(text);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        this.errorMsg.set(err?.message || this.translate.get('support.error'));
      }
    } finally {
      this.sending.set(false);
      this.abortController = null;
      this.shouldScroll = true;
    }
  }

  private async stream(message: string): Promise<void> {
    const user = getAuth().currentUser;
    if (!user) {
      this.errorMsg.set(this.translate.get('support.notAuthenticated'));
      return;
    }
    const idToken = await user.getIdToken();
    const projectId = environment.firebase.projectId;
    const endpoint = `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/runSupportAgent`;

    this.abortController = new AbortController();
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        sessionId: this.sessionId,
        message,
        app: 'build',
        orgId: this.orgContext.currentOrg()?.id ?? undefined,
      }),
      signal: this.abortController.signal,
    });

    if (resp.status === 400) {
      throw new Error(this.translate.get('support.noOrg'));
    }
    if (resp.status === 401) {
      this.sessionId = null;
      throw new Error(this.translate.get('support.notAuthenticated'));
    }
    if (resp.status === 402) {
      throw new Error(this.translate.get('support.noCredits'));
    }
    if (resp.status === 403) {
      this.sessionId = null;
      throw new Error(this.translate.get('support.error'));
    }
    if (!resp.ok || !resp.body) {
      const body = await resp.text().catch(() => '');
      throw new Error(`Server ${resp.status}: ${body || resp.statusText}`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            this.handleEvent(event);
          } catch {
            // ignore malformed frame
          }
        }
      }
    }
  }

  private handleEvent(event: any): void {
    const type = event?.type as string | undefined;
    if (!type) return;

    if (type === 'token' && typeof event.token === 'string') {
      this.appendToLastAssistant(event.token);
      this.shouldScroll = true;
      return;
    }

    if (type === 'run-started' && typeof event.runId === 'string') {
      this.currentRunId = event.runId;
      return;
    }

    if (type === 'error' && typeof event.error === 'string') {
      const code = event.error;
      const knownKey = `support.${code}`;
      const translated = this.translate.get(knownKey);
      this.errorMsg.set(translated && translated !== knownKey ? translated : code);
      return;
    }
    // 'done' — no UI state needed; outer loop exits when reader is done.
  }

  private appendToLastAssistant(chunk: string) {
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== 'assistant') {
      this.messages.set([...msgs, { role: 'assistant', content: chunk }]);
      return;
    }
    const updated: ChatMessage = { role: 'assistant', content: last.content + chunk };
    this.messages.set([...msgs.slice(0, -1), updated]);
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  private resetSession() {
    this.abortController?.abort();
    this.abortController = null;
    this.sessionId = null;
    this.currentRunId = null;
    this.messages.set([]);
    this.errorMsg.set('');
    this.sending.set(false);
  }
}
